<template>
  <div class="chart-wrapper-page">
    <!-- Chart Card -->
    <div class="chart-card">
      <div class="chart-header">
        <h3 class="chart-title">Customer Churn</h3>
        <div class="year-filters">
          <button
            :class="['year-btn', { active: selectedYear === getLastYearRange }]"
            @click="fetchData(getLastYearRange)"
          >
            {{ this.getLastYearRange[0].substring(0, 4) }}
          </button>
          <button
            :class="['year-btn', { active: selectedYear === getThisYearRange }]"
            @click="fetchData(getThisYearRange)"
          >
            {{ this.getThisYearRange[0].substring(0, 4) }}
          </button>
          <button
            :class="['year-btn', { active: selectedYear === getNextYearRange }]"
            @click="fetchData(getNextYearRange)"
          >
            {{ this.getNextYearRange[0].substring(0, 4) }}
          </button>
        </div>
      </div>

      <div class="chart-container">
        <div v-if="isLoading" class="loading-state">
          <icon name="spinner" spin class="spinner"></icon>
          <p>Loading chart data...</p>
        </div>
        <canvas v-show="!isLoading" ref="churnChart"></canvas>
        <p v-if="!isLoading" class="chart-hint">Click a New, Reactivated or Churned bar to see the customers behind it.</p>
      </div>

      <p v-if="!isLoading" class="formula-hint">
        <strong>Churn rate</strong> = churned customers &divide; active customers at the start of the month &times; 100.
        The active base is a running total: seeded once from everyone converted (and not yet churned) right before the selected period,
        then carried forward each month as <em>+ new &minus; churned</em>.
      </p>
    </div>

    <!-- Active Customers Card -->
    <div class="chart-card">
      <div class="chart-header">
        <h3 class="chart-title">Active Customers</h3>
      </div>

      <div class="chart-container">
        <div v-if="isLoading" class="loading-state">
          <icon name="spinner" spin class="spinner"></icon>
          <p>Loading chart data...</p>
        </div>
        <canvas v-show="!isLoading" ref="activeChart"></canvas>
        <p v-if="!isLoading" class="chart-hint">Running total of active customers, with new conversions per month. Click a bar to see who converted.</p>
      </div>
    </div>

    <b-modal
      centered
      size="xl"
      :visible="showModal"
      hide-footer
      title-tag="div"
      @hide="closeModal"
    >
      <template #modal-title>
        <span :class="['movement-badge', modalMovement]">{{ movementLabel }}</span>
        {{ modalMonthLabel }}
        <span class="modal-count">({{ modalCustomers.length }})</span>
      </template>

      <div v-if="modalLoading" class="modal-loading">
        <icon name="spinner" spin class="spinner"></icon>
        <p>Loading customers...</p>
      </div>
      <div v-else-if="modalCustomers.length === 0" class="modal-empty">
        No customers found for this month.
      </div>
      <table v-else class="customer-list-table">
        <thead>
          <tr>
            <th>Company</th>
            <th>Contact</th>
            <th>Email</th>
            <th>Source</th>
            <th v-if="modalMovement === 'churned'">Converted</th>
            <th>{{ modalMovement === 'churned' ? 'Reason / Date' : 'Converted' }}</th>
            <th v-if="modalMovement === 'churned'">Active for</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="customer in modalCustomers" :key="customer.id">
            <td>
              <router-link :to="{ name: 'customer', params: { id: customer.id } }">
                {{ customer.company_name }}
              </router-link>
            </td>
            <td>{{ customer.contact_first_name }} {{ customer.contact_last_name }}</td>
            <td>{{ customer.contact_email }}</td>
            <td>{{ customer.source || '-' }}</td>
            <td v-if="modalMovement === 'churned'">{{ formatDate(customer.converted_at) }}</td>
            <td>{{ movementDetail(customer) }}</td>
            <td v-if="modalMovement === 'churned'">{{ activeDuration(customer) }}</td>
          </tr>
        </tbody>
      </table>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Chart from "chart.js";

import "vue-awesome/icons/spinner";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
  },
  data() {
    return {
      chartData: { labels: [], datasets: [] },
      isLoading: false,
      selectedYear: null,
      chartInstance: null,
      activeChartInstance: null,
      showModal: false,
      modalLoading: false,
      modalMovement: "new",
      modalMonthLabel: "",
      modalCustomers: []
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleFetch"]),
    fetchData(range) {
      this.selectedYear = range;
      const data = {
        query: `
          query  {
            CustomerChurnMovement(
              from: "${range[0]}",
              to: "${range[1]}"
            )
          }
        `
      };
      this.isLoading = true;
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) {
            this.chartData = JSON.parse(res.CustomerChurnMovement);
          }
        })
        .finally(() => {
          this.isLoading = false;
          this.$nextTick(() => {
            this.renderChart();
            this.renderActiveChart();
          });
        });
    },
    resolveClickedBar(elements, datasets) {
      if (!elements || !elements.length) return null;

      const barElement = elements.find(el => {
        const dataset = datasets[el._datasetIndex];
        return dataset && dataset.type === "bar";
      });
      if (!barElement) return null;

      return { dataset: datasets[barElement._datasetIndex], index: barElement._index };
    },
    renderChart() {
      if (!this.$refs.churnChart) return;

      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const ctx = this.$refs.churnChart.getContext("2d");
      const datasets = this.chartData.datasets.filter(d => d.label !== "Active customers");

      this.chartInstance = new Chart(ctx, {
        type: "bar",
        data: { labels: this.chartData.labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [{
              gridLines: { display: false }
            }],
            yAxes: [
              {
                id: "y",
                position: "left",
                gridLines: { color: "#E2E8F0" },
                ticks: {
                  callback: (value) => value
                }
              },
              {
                id: "y1",
                position: "right",
                gridLines: { drawOnChartArea: false },
                ticks: {
                  callback: (value) => value + "%"
                }
              }
            ]
          },
          legend: {
            display: true,
            position: "bottom",
            labels: { boxWidth: 12, padding: 15 }
          },
          tooltips: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: (tooltipItem, chartData) => {
                const dataset = chartData.datasets[tooltipItem.datasetIndex];
                const label = dataset.label || "";
                const suffix = dataset.yAxisID === "y1" ? "%" : "";
                return `${label}: ${tooltipItem.yLabel}${suffix}`;
              }
            }
          },
          onHover: (event, elements) => {
            event.target.style.cursor = elements.length ? "pointer" : "default";
          },
          onClick: (event) => {
            // Chart.js's default hit-testing (hover.mode: 'nearest') can pick the
            // wrong dataset on a mixed bar+line chart with two side-by-side bars.
            // Hit-test the click point directly against each dataset instead.
            const points = this.chartInstance.getElementsAtEventForMode(
              event, "point", { intersect: true }, false
            );
            const hit = this.resolveClickedBar(points, datasets);
            if (!hit) return;

            let movement = null;
            if (hit.dataset.label === "New customers") movement = "new";
            if (hit.dataset.label === "Reactivated") movement = "reactivated";
            if (hit.dataset.label === "Churned customers") movement = "churned";
            if (!movement) return; // Net / Churn rate lines aren't clickable lists

            this.openCustomerList(movement, hit.index);
          }
        }
      });
    },
    renderActiveChart() {
      if (!this.$refs.activeChart) return;

      if (this.activeChartInstance) {
        this.activeChartInstance.destroy();
      }

      const ctx = this.$refs.activeChart.getContext("2d");
      const activeDs = this.chartData.datasets.find(d => d.label === "Active customers");
      const newDs = this.chartData.datasets.find(d => d.label === "New customers");
      if (!activeDs || !newDs) return;

      const datasets = [
        { ...activeDs, fill: false, borderWidth: 3, pointRadius: 3 },
        { ...newDs, yAxisID: "y1" }
      ];

      this.activeChartInstance = new Chart(ctx, {
        type: "line",
        data: { labels: this.chartData.labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [{
              gridLines: { display: false }
            }],
            yAxes: [
              {
                id: "y",
                position: "left",
                gridLines: { color: "#E2E8F0" },
                ticks: { beginAtZero: true }
              },
              {
                id: "y1",
                position: "right",
                gridLines: { drawOnChartArea: false },
                ticks: { beginAtZero: true }
              }
            ]
          },
          legend: {
            display: true,
            position: "bottom",
            labels: { boxWidth: 12, padding: 15 }
          },
          tooltips: {
            mode: "index",
            intersect: false
          },
          onHover: (event, elements) => {
            event.target.style.cursor = elements.length ? "pointer" : "default";
          },
          onClick: (event) => {
            const points = this.activeChartInstance.getElementsAtEventForMode(
              event, "point", { intersect: true }, false
            );
            const hit = this.resolveClickedBar(points, datasets);
            if (!hit || hit.dataset.label !== "New customers") return;

            this.openCustomerList("new", hit.index);
          }
        }
      });
    },
    monthRangeForIndex(index) {
      // selectedYear[0] is a plain "YYYY-M-D" string (e.g. "2026-1-1"). Parsing it with
      // `new Date()` treats it as local midnight, which then reads back one month early
      // via getUTC* in any UTC+ timezone (Jan 1 local -> Dec 31 UTC), shifting every
      // bucket by a month. Parse the parts directly instead.
      const [year, month] = this.selectedYear[0].split("-").map(Number);
      const base = month - 1;

      const from = new Date(Date.UTC(year, base + index, 1)).toISOString().substring(0, 10);
      const to = new Date(Date.UTC(year, base + index + 1, 1)).toISOString().substring(0, 10);

      return { from, to };
    },
    openCustomerList(movement, index) {
      this.modalMovement = movement;
      this.modalMonthLabel = this.chartData.labels[index];
      this.showModal = true;
      this.modalLoading = true;
      this.modalCustomers = [];

      const { from, to } = this.monthRangeForIndex(index);
      const data = {
        query: `
          query {
            CustomerChurnMovementCustomers(
              from: "${from}",
              to: "${to}",
              movement: "${movement}"
            )
          }
        `
      };

      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) {
            this.modalCustomers = JSON.parse(res.CustomerChurnMovementCustomers || "[]");
          }
        })
        .finally(() => {
          this.modalLoading = false;
        });
    },
    closeModal() {
      this.showModal = false;
      this.modalCustomers = [];
    },
    movementDetail(customer) {
      if (this.modalMovement !== "churned") {
        return this.formatDate(customer.converted_at);
      }
      // event_at / event_type come straight from the lifecycle event that put this
      // customer in the bucket — not from the mutable customers.* stamps, which can be
      // stale (e.g. an old terminated_at on a customer whose latest churn was a cancel).
      if (customer.event_at) {
        const verb = customer.event_type === "cancelled" ? "Cancelled" : "Terminated";
        return `${verb} ${this.formatDate(customer.event_at)}`;
      }
      return "-";
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString();
    },
    activeDuration(customer) {
      const churnDate = customer.event_at;
      if (!customer.converted_at || !churnDate) return "-";

      const start = new Date(customer.converted_at);
      const end = new Date(churnDate);

      let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      if (end.getDate() < start.getDate()) months -= 1;
      months = Math.max(months, 0);

      if (months < 1) {
        const days = Math.max(Math.round((end - start) / (1000 * 60 * 60 * 24)), 0);
        return `${days}d`;
      }

      const years = Math.floor(months / 12);
      const remMonths = months % 12;
      if (years < 1) return `${remMonths}m`;
      return remMonths > 0 ? `${years}y ${remMonths}m` : `${years}y`;
    }
  },
  computed: {
    ...mapGetters([
      "getThisYearRange",
      "getLastYearRange",
      "getNextYearRange",
      "getUserSystem"
    ]),
    movementLabel() {
      return {
        new: "New",
        reactivated: "Reactivated",
        churned: "Churned"
      }[this.modalMovement] || this.modalMovement;
    }
  },
  watch: {
    getUserSystem: function() {
      this.fetchData(this.getThisYearRange);
    }
  },
  mounted() {
    this.selectedYear = this.getThisYearRange;
    this.fetchData(this.getThisYearRange);
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
    if (this.activeChartInstance) {
      this.activeChartInstance.destroy();
    }
  }
};
</script>

<style scoped>
.chart-wrapper-page {
  padding: 0;
  background-color: transparent;
}

.chart-card {
  background: white;
  padding: 20px;
}

.chart-card + .chart-card {
  margin-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.year-filters {
  display: flex;
  gap: 8px;
  background: #f7fafc;
  padding: 4px;
  border-radius: 8px;
}

.year-btn {
  padding: 8px 20px;
  border: none;
  background: transparent;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;
}

.year-btn:hover {
  background-color: #edf2f7;
}

.year-btn.active {
  background-color: #5469d4;
  color: white;
}

.chart-container {
  height: 400px;
  position: relative;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #718096;
}

.loading-state .spinner {
  font-size: 32px;
  margin-bottom: 12px;
  color: #5469d4;
}

.loading-state p {
  margin: 0;
  font-size: 14px;
}

.chart-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #a0aec0;
  text-align: center;
}

.formula-hint {
  margin: 16px 0 0;
  padding: 12px 16px;
  background: #f7fafc;
  border-left: 3px solid #5469d4;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #4a5568;
}

.formula-hint strong {
  color: #2d3748;
}

.movement-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  margin-right: 8px;
}

.movement-badge.new {
  background-color: #48bb78;
}

.movement-badge.reactivated {
  background-color: #38b2ac;
}

.movement-badge.churned {
  background-color: #f56565;
}

.modal-count {
  color: #a0aec0;
  font-weight: 400;
  font-size: 14px;
}

.modal-loading,
.modal-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #718096;
}

.customer-list-table {
  width: 100%;
  border-collapse: collapse;
}

.customer-list-table th {
  text-align: left;
  font-size: 12px;
  text-transform: uppercase;
  color: #a0aec0;
  border-bottom: 2px solid #e2e8f0;
  padding: 8px 12px;
}

.customer-list-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #edf2f7;
  font-size: 14px;
}

.customer-list-table tbody tr:hover {
  background-color: #f7fafc;
}

@media (max-width: 768px) {
  .chart-wrapper-page {
    padding: 0;
  }

  .chart-card {
    padding: 16px;
  }

  .chart-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .year-filters {
    width: 100%;
  }

  .year-btn {
    flex: 1;
  }

  .chart-container {
    height: 300px;
  }
}
</style>
